import { ConversationErrorCode } from 'generated-graphql/types';

export const DEFAULT_DELIVERY_FAILED_TOOLTIP =
  'Not delivered. Check contact details.';

/**
 * Maps a `ConversationErrorCode` to the operator-facing tooltip string
 * shown on a `DELIVERY_FAILED` conversation.
 *
 * Two distinct shapes of failure live in this enum:
 *
 *  - Validation-shape codes (`INVALID_*` / `MISSING_*`) — the contact
 *    info itself is the problem; "check contact details" is the
 *    correct call to action.
 *  - Delivery-shape codes (`PHONE_DELIVERY_FAILED` /
 *    `EMAIL_DELIVERY_FAILED`, added in PAD-5118 / PAD-5119) — the
 *    contact info was accepted but the carrier / mail server rejected
 *    the message. "Check contact details" would send the operator
 *    chasing the wrong thing; the tooltip names the real reason
 *    instead.
 *
 * Null / undefined errorCode (older conversations, pre-fetch state)
 * falls back to the legacy copy.
 *
 * The switch is exhaustive over `ConversationErrorCode`: the
 * `_exhaustive: never` assignment in the default branch is a
 * build-time guard. If a new enum value is added upstream without a
 * corresponding case here, `tsc` will fail with "Type 'X' is not
 * assignable to type 'never'." — i.e. silent fall-through to the
 * default copy is impossible at build time.
 */
export const errorCodeToTooltip = (
  errorCode: ConversationErrorCode | null | undefined
): string => {
  if (errorCode === null || errorCode === undefined) {
    return DEFAULT_DELIVERY_FAILED_TOOLTIP;
  }

  switch (errorCode) {
    case ConversationErrorCode.InvalidPhoneNumber:
    case ConversationErrorCode.MissingPhoneNumber:
      return 'Not delivered — phone number is invalid or missing. Check contact details.';
    case ConversationErrorCode.InvalidEmailAddress:
    case ConversationErrorCode.MissingEmailAddress:
      return 'Not delivered — email address is invalid or missing. Check contact details.';
    case ConversationErrorCode.PhoneDeliveryFailed:
      return 'Not delivered — carrier rejected the message (e.g. region restricted, unreachable handset, spam filter).';
    case ConversationErrorCode.EmailDeliveryFailed:
      return 'Not delivered — recipient mail server rejected the message (e.g. mailbox full, content rejected).';
    default: {
      // Build-time exhaustiveness guard — see the JSDoc above. The
      // assignment compiles only if every enum value is handled by a
      // case above. Runtime returns the legacy copy as a defensive
      // fallback if type erasure ever lets an unhandled value through.
      const _exhaustive: never = errorCode;
      void _exhaustive;
      return DEFAULT_DELIVERY_FAILED_TOOLTIP;
    }
  }
};
